/**
 * BizFirst Constants
 *
 * Shared constants across all BizFirst applications
 */

import type { Theme, SpacingScale, Typography, Shadows, BorderRadius, Transitions, ZIndexScale } from '../types'

/**
 * Dark Theme Definition
 */
export const DARK_THEME: Theme = {
  name: 'dark',
  displayName: 'Dark',
  colors: {
    primary: '#1DB954',
    primaryHover: '#17A74A',
    primaryActive: '#148C3E',
    primarySoft: '#163D2A',
    primaryGlow: 'rgba(29, 185, 84, 0.25)',

    bg: '#0d1117',
    bgSecondary: '#1c1f26',
    bgTertiary: '#30363d',
    bgOverlay: 'rgba(0, 0, 0, 0.7)',

    textPrimary: '#e6edf3',
    textSecondary: '#8b949e',
    textMuted: '#6e7681',
    textInverse: '#0d1117',

    border: '#30363d',
    borderLight: '#444c56',
    borderLighter: '#d0d7de',

    success: '#1DB954',
    successLight: 'rgba(29, 185, 84, 0.1)',
    successGlow: 'rgba(29, 185, 84, 0.25)',

    warning: '#f0883e',
    warningLight: 'rgba(240, 136, 62, 0.1)',
    warningGlow: 'rgba(240, 136, 62, 0.25)',

    error: '#f85149',
    errorLight: 'rgba(248, 81, 73, 0.1)',
    errorGlow: 'rgba(248, 81, 73, 0.25)',

    info: '#0099cc',
    infoLight: 'rgba(0, 153, 204, 0.1)',
    infoGlow: 'rgba(0, 153, 204, 0.25)',

    controlBg: '#1c1f26',
    controlBgNested: '#30363d',
    controlBgDeep: '#444c56',
    controlDivider: '#30363d',
    inputBg: 'rgba(0, 0, 0, 0.3)',
    inputBorder: '#30363d',
    inputColor: '#e6edf3',
  },
  cssFile: './dark/index.css',
}

/**
 * Light Theme Definition
 */
export const LIGHT_THEME: Theme = {
  name: 'light',
  displayName: 'Light',
  colors: {
    primary: '#1DB954',
    primaryHover: '#17A74A',
    primaryActive: '#148C3E',
    primarySoft: '#e8f5e9',
    primaryGlow: 'rgba(29, 185, 84, 0.15)',

    bg: '#ffffff',
    bgSecondary: '#f5f5f5',
    bgTertiary: '#eeeeee',
    bgOverlay: 'rgba(0, 0, 0, 0.3)',

    textPrimary: '#1a1a1a',
    textSecondary: '#666666',
    textMuted: '#999999',
    textInverse: '#ffffff',

    border: '#dddddd',
    borderLight: '#cccccc',
    borderLighter: '#bbbbbb',

    success: '#1DB954',
    successLight: 'rgba(29, 185, 84, 0.1)',
    successGlow: 'rgba(29, 185, 84, 0.2)',

    warning: '#f0883e',
    warningLight: 'rgba(240, 136, 62, 0.1)',
    warningGlow: 'rgba(240, 136, 62, 0.2)',

    error: '#f85149',
    errorLight: 'rgba(248, 81, 73, 0.1)',
    errorGlow: 'rgba(248, 81, 73, 0.2)',

    info: '#0099cc',
    infoLight: 'rgba(0, 153, 204, 0.1)',
    infoGlow: 'rgba(0, 153, 204, 0.2)',

    controlBg: '#f5f5f5',
    controlBgNested: '#eeeeee',
    controlBgDeep: '#e8e8e8',
    controlDivider: '#dddddd',
    inputBg: '#ffffff',
    inputBorder: '#cccccc',
    inputColor: '#1a1a1a',
  },
  cssFile: './white/index.css',
}

/**
 * Theme Map
 */
export const THEMES = {
  dark: DARK_THEME,
  light: LIGHT_THEME,
} as const

/**
 * Spacing Scale
 */
export const SPACING: SpacingScale = {
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  '2xl': '3rem',   // 48px
}

/**
 * Typography
 */
export const TYPOGRAPHY: Typography = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontFamilyHeading: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontMono: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeights: {
    tight: 1.2,
    normal: 1.6,
    relaxed: 1.8,
  },
  letterSpacings: {
    tight: '-0.01em',
    normal: '0',
    relaxed: '0.02em',
  },
}

/**
 * Shadows
 */
export const SHADOWS: Shadows = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 25px rgba(0, 0, 0, 0.15)',
  xl: '0 20px 40px rgba(0, 0, 0, 0.2)',
  glow: '0 0 20px rgba(29, 185, 84, 0.25)',
}

/**
 * Border Radius
 */
export const BORDER_RADIUS: BorderRadius = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
}

/**
 * Transitions
 */
export const TRANSITIONS: Transitions = {
  fast: '150ms ease',
  base: '250ms ease',
  slow: '350ms ease',
  linear: 'linear',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
}

/**
 * Z-Index Scale
 */
export const Z_INDEX: ZIndexScale = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  notification: 1080,
}

/**
 * Storage Key for Theme Preference
 */
export const THEME_STORAGE_KEY = 'bizfirst-theme'

/**
 * Name of the window CustomEvent dispatched after every theme change (detail: { theme }).
 */
export const THEME_CHANGE_EVENT = 'bizfirst:theme-change'

/**
 * CSS Variable Names
 */
export const CSS_VARIABLES = {
  primary: '--color-primary',
  bg: '--color-bg',
  bgSecondary: '--color-bg-secondary',
  textPrimary: '--color-text-primary',
  textSecondary: '--color-text-secondary',
  border: '--color-border',
  success: '--color-success',
  warning: '--color-warning',
  error: '--color-error',
  info: '--color-info',
} as const

/**
 * Data attribute for theme
 */
export const THEME_DATA_ATTRIBUTE = 'data-theme'

/**
 * Theme class prefix
 */
export const THEME_CLASS_PREFIX = 'theme-'

export default {
  DARK_THEME,
  LIGHT_THEME,
  THEMES,
  SPACING,
  TYPOGRAPHY,
  SHADOWS,
  BORDER_RADIUS,
  TRANSITIONS,
  Z_INDEX,
  THEME_STORAGE_KEY,
  THEME_CHANGE_EVENT,
  CSS_VARIABLES,
  THEME_DATA_ATTRIBUTE,
  THEME_CLASS_PREFIX,
}
